import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    FormGroup,
    FormControl,
    FormBuilder,
    Validators,
    AbstractControl
} from "@angular/forms";
import { SSTService } from '@app/feature/main/roles-shared/services/self-service-tab.service';
import { GraphAxis } from '@app/shared/constants/graphAxis';
import { GraphData } from '@app/feature/main/roles-shared/models/graphData';
import { AuthenticationService } from "@app/shared/services/authentication.service";
import { MethodofOperation } from "@app/shared/constants/methodofOperation";
import swal from 'sweetalert2';
import * as _ from 'lodash';

declare var $: any;
@Component({
    selector: 'self-service-tab',
    templateUrl: './self-service-tab.component.html',
    styleUrls: ['./self-service-tab.component.css']
})

export class SelfServiceTabComponent implements OnInit,OnDestroy {

    initalCheck: boolean = true;
    graphGenerated: boolean = false;
    startOver: boolean = false;
    canGraphGenerate: boolean = false;
    formSubmitted: boolean = false;
    chartData: any = {};
    instructionMessage: string[] = [
        'assets/images/sst-xaxis.png',
        'assets/images/sst-yaxis-mop.png'
    ];
    instructionMessageIndex: number = 0;
    researchModuleId: string;
    researchModuleSST: any;
    xAxisSelection: any;
    yAxisSelection: any;
    graphTypes: any[];
    xAxisQuestions: any[];
    userRole;
    yAxisQuestions: any[];
    selectedMethodOfOperation: string;
    mergeGraphList: any = [];
    saveGraphForm: FormGroup;
    sstContent:any;
    chartSelectedType: string;

    mOOperationMapping = MethodofOperation.mapping;
    keys: any = GraphAxis.UserDataKeywords;
    xAxisUserProfile: any[];
    yAxisUserProfile: any[];
    graphDataList: any[];
    choiceList: any[];
    selectedChoiceItems: any[];
    methodOfOperation: any[];
    methodOfOperationId: string;
    chartSelected: any;
    xAxisDataValue: any;
    yAxisDataValue: any;
    selectionProcess: any[];
    resetMop = false;
    filteredYAxisIds = [];;
    toast = swal.mixin({
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 5000
    });

    constructor(
        private sstService: SSTService,
        private authenticationService: AuthenticationService,
        private route: ActivatedRoute,
        private router: Router,
        public formBuilder: FormBuilder
    ) { }

    ngOnInit() {
        this.researchModuleId = this.route.snapshot.paramMap.get("id");
        this.sstService.getSSTById(this.researchModuleId).subscribe(sstData => {
            this.researchModuleSST = sstData;
            this.formatSSTData();
        })
        this.createForm();
        if (this.authenticationService.isLoggedIn()) {
            this.userRole = this.authenticationService.getFormattedRole();
        }
    }

    createForm() {
        this.saveGraphForm = this.formBuilder.group({
            title: ["", [Validators.required]],
            description: ["", [Validators.required]],
            xLabel: ["", []],
            yLabel: ["", []]
        });
    }

    formatSSTData() {
        this.graphTypes = this.researchModuleSST.graphTypes.map(graph => {
            let graphAxis = GraphAxis.data.find(item => item.type == graph.title);
            return {
                id: graph.id,
                graphTypeId: graph.graphTypeId,
                graphType: graph.graphType,
                title: graph.title,
                axis: graphAxis.axis,
                type: graphAxis.type,
                image: graph.iconUrl,
                selectable: true
            }
        });
        this.xAxisQuestions =  this._formatAxisQuestionData('xAxis');
        this.yAxisQuestions =  this.removeDuplicates(this._formatAxisQuestionData('yAxis'));
        this.xAxisUserProfile = _.sortBy(this._formatAxisProfileData('xAxis'), [function(data: any) { return data.userProfile; }]);
        this.yAxisUserProfile = _.sortBy(this._formatAxisProfileData('yAxis'), [function(data: any) { return data.userProfile; }]);
        this.methodOfOperation = this.researchModuleSST.methodOfOperations.map(method => {
            let icon: string;
            switch (method.method) {
                case 'Percentage': {
                    icon = 'percentage';
                    break;
                }
                case 'Mean': {
                    icon = 'x-dash';
                    break;
                }
                case 'Proportion': {
                    icon = 'square-dots';
                    break;
                }
                case 'Frequency': {
                    icon = 'oval';
                    break;
                }
            }
            return {
                id: method.id,
                methodOfOperationId: method.methodOfOperationId,
                method: method.method,
                activeIcon: `assets/images/mop-icons/active/${icon}.svg`,
                inActiveIcon: `assets/images/mop-icons/${icon}.svg`,
                selected: false
            }
        })
    }
    // Todo : Quick fix for avoid duplicate in y axis questions
    removeDuplicates(data){
      let uniqueData =[];
    if(data && data.length>0){
      for(let i=0;i<data.length;i++){
        let duplicateIndex = uniqueData.findIndex(u=> u.id === data[i].id && u.label === data[i].label);
        if(duplicateIndex===-1){
          uniqueData.push(data[i]);
        }
      }
    }
    return uniqueData;
    }

    _formatAxisQuestionData(type) {
        return this.researchModuleSST.questions.filter(question => {
            if (question.axis == type) {
                return true;
            }
        }).map(question => {
            let choices = question.choices.map(choice => {
                return {
                    id: choice.id,
                    choiceId: choice.choiceId,
                    text: choice.text,
                    sequence: choice.sequence,
                    selected: false
                }
            })
            return {
                id: question.questionId,
                label: question.label,
                questionId: question.questionId,
                choices: choices,
                selected: false
            }
        });
    }

    _formatAxisProfileData(type) {
        return this.researchModuleSST.userProfile.filter(profile => {
            if (profile.axis == type) {
                return true;
            }
        }).map(profile => {
            let axisData = GraphAxis.UserDataKeywords.find(graph => graph.sst == profile.userProfile);
            let profileDatas = profile.userProfileData.map(profileData => {
                return {
                    id: profileData.id,
                    dataId: profileData.dataId,
                    sequence: profileData.sequence,
                    title: profileData.title,
                    selected: false
                }
            });
            if ( axisData.sst === 'employee_size' || axisData.sst === "revenue" || axisData.sst === "it_budget" || axisData.sst === "job_title" ) {
                profileDatas = _.sortBy(profileDatas, [function(data: any) { return data.sequence; }]);
            }
            return {
                id: profile.id,
                userProfile: profile.userProfile,
                title: axisData.type,
                userProfileData: profileDatas,
                selected: false
            }
        });
    }

    axisSelected(type) {
    }

    graphSelection(data) {
        this.chartSelected = data;
        if(!this.graphGenerated){ //check if already a graph is generated
            this.chartSelected.axis.forEach(axisItem => { //TODO:  check if the axis is same
                this.chartData[axisItem] = new GraphData();
            });
            this.canGenerateGraph();
        } // Todo: to remove below code after fixing the reset issue in upcomming release
        // else{
        //     this.resetSST(false,data);
        // }
    }

    axisData(axisData, key) {
        this.startOver = false;
        if(key == 'X'){
            this.xAxisDataValue = this.getLabel(axisData);
            if(axisData && axisData.questionSelected && axisData.question && axisData.question.questionId){
            this.sstService.getSSTQuestionYaxisFilterQuestions(axisData.question.questionId).subscribe((resp:any)=>{
             if(resp && resp.data){
                 this.filteredYAxisIds = resp.data.map(x=>x.questionId);
             }else{
                this.filteredYAxisIds = [];
             }
            },err=>{

            });
        }else{
          this.filteredYAxisIds = [];
        }
        }else{
        this.yAxisDataValue = this.getLabel(axisData);
        }
        this.instructionMessageIndex = 1;

        this.chartData[key] = this._getAxisData(axisData)
        if (axisData.questionSelected)
            this.getChoiceList(axisData.question, key);
        this.chartData[key]['selectedChoiceItemsPD'] = axisData.profileSelected ?
            this._fetchSelectedId(axisData.userProfile.userProfileData, 'dataId') : [];
        if(axisData.isApplied){
        this.canGenerateGraph();
        }else{
          this.graphGenerated = false;
          this.canGraphGenerate =false;
        }
    }

    graphGeneration(method) {
        this.selectedMethodOfOperation = method.method;
        this._updateMOP(method);
        this._processSSTContent();
        this.getGraphData();
    }

    canGenerateGraph() {
        this.chartSelected.axis.forEach(axisItem => {
            if (this.chartData[axisItem].isQuestionData || this.chartData[axisItem].isUserProfileData) {
                this.canGraphGenerate = true;
            }
        });
    }

    getGraphData() {
        this.chartData.graphTypeId = this.chartSelected.graphTypeId;
        this.sstService.graphDetails(this.sstContent).subscribe(graphResponse => {
            // if (graphResponse.numberOfResponse) {
            // //   this.NVlaue = graphResponse.numberOfResponse;
            // }
            let needsFormatting = false;
            // this.chartSelected.axis.forEach(axisItem => {
            //     if (this.chartData[axisItem].singleChoice) {
            //         needsFormatting = true;
            //     }
            // });

            if (!needsFormatting) {
                this.mergeGraphList = this.chartData['X'].choice.choiceList.map(choice => {
                    let obj = this.generateObject(graphResponse['data'] ? graphResponse['data'] : [], choice);
                    return {...choice, ...obj};
                })
            } else {
                let response: any = [];
                if (graphResponse['data']) {
                    graphResponse['data'].forEach(x => {
                        response.push({
                            key: x.choice,
                            [this.mOOperationMapping[this.selectedMethodOfOperation]]:
                                x.grouping[0][
                                this.mOOperationMapping[this.selectedMethodOfOperation]
                                ]
                        });
                    });
                }

                this.mergeGraphList = this.chartData['X'].choice.choiceList.map(choice => {
                    return this.generateObject(response, choice);
                })
            }
            this._processSSTContent();
            this.processGraphData(); //will process the graph Data , no need of secondaryChoiceList
        },
            error => {
                this.toast({
                  type: 'info',
                  text: 'Please try other options to generate graph!!'
                });
            })
    }

    _processSSTContent(){
        let sstContent : any = {};
        this.chartSelected.axis.forEach(axisItem => {
            sstContent[axisItem] = new GraphData();
            sstContent[axisItem]['methodOfOperationId'] = this.chartData[axisItem]['methodOfOperationId'];
            sstContent[axisItem]['isQuestionData'] = this.chartData[axisItem]['isQuestionData'];
            sstContent[axisItem]['questionId'] = this.chartData[axisItem]['questionId'];
            sstContent[axisItem]['singleChoice'] = this.chartData[axisItem]['singleChoice'];
            sstContent[axisItem]['selectedChoices'] = this.chartData[axisItem]['choice'] ? this.chartData[axisItem]['choice']['selectedChoiceItemsQL']:[];
            sstContent[axisItem]['userProfileData'] = this.chartData[axisItem]['userProfileData'];
            sstContent[axisItem]['isUserProfileData'] = false;//Todo : this will handle in future
            sstContent[axisItem]['industryId'] = this.chartData[axisItem]['industryId'];
            sstContent[axisItem]['itBudgetId'] = this.chartData[axisItem]['itBudgetId'];
            sstContent[axisItem]['geographyId'] = this.chartData[axisItem]['geographyId'];
            sstContent[axisItem]['lineOfBusinessId'] = this.chartData[axisItem]['lineOfBusinessId'];
            sstContent[axisItem]['countryId'] = this.chartData[axisItem]['countryId'];
            sstContent[axisItem]['jobTitleId'] = this.chartData[axisItem]['jobTitleId'];
            sstContent[axisItem]['globalRevenueId'] = this.chartData[axisItem]['globalRevenueId'];
            sstContent[axisItem]['employeeSizeId'] = this.chartData[axisItem]['employeeSizeId'];
        });
        this.sstContent = {
            ...sstContent,
            ...{graphTypeId: this.chartSelected.graphTypeId,researchDocumentId:this.researchModuleId}
        };
    }

    // process data for display graph
    processGraphData() {
        this.graphDataList = [];
        let defaultCountInitializedList = [];
        //checkout response and initialise count value to zero if not
        for (let gList of this.mergeGraphList) {
            if (gList[this.mOOperationMapping[this.selectedMethodOfOperation]]) {
                defaultCountInitializedList.push(gList);
            } else {
                gList[this.mOOperationMapping[this.selectedMethodOfOperation]] = 0;
                defaultCountInitializedList.push(gList);
            }
        }

        this.graphDataList = defaultCountInitializedList.filter(item => {
          //Todo: below code for future feature
            // if (this.chartData.X.isUserProfileData) {
            //     return this.chartData.X.selectedChoiceItemsPD.includes(item.key);
            // } else {
            //     return this.chartData.X.choice.selectedChoiceItemsQL.includes(item.key);
            // }
            return this.chartData.X.choice.selectedChoiceItemsQL.includes(item.key);
        });

        // this.chartLabels = {
        //     X: this.chartLabels.X,
        //     Y: this.chartLabels.Y
        // };
        this._ifGraphGenerated();
        this.chartSelectedType = this.chartSelected.type;
        return this.graphDataList;
    }

    _ifGraphGenerated() {
        this.graphGenerated = true;
        this.graphTypes = this.graphTypes.map(graph => {
            let selectable: boolean = false;
            if(this.chartSelected.axis.length == graph.axis.length &&
                this.chartSelected.axis.every(axis => graph.axis.includes(axis)))
                selectable = true;
            else
                selectable = false;
            return {...graph, ...{selectable: selectable}}
        })
    }

    generateObject(graphResponse, obj) {
        const data = graphResponse.find(graph => graph.key == obj.id);
        if (data === undefined) {
            obj[this.mOOperationMapping[this.selectedMethodOfOperation]] = 0;
            return obj;
        } else {
            return data;
        }
    }

    getChoiceList(question, axis) {
        let choiceList, selectedChoiceItems;
        this.choiceList = [];
        this.sstService.responseList(question.questionId || question.id).subscribe((resp: any) => {
            choiceList = resp.choices;
            if (choiceList.length !== 0) {
                selectedChoiceItems = this._fetchSelectedId(question.choices, 'choiceId');
            }
            this.chartData[axis]['choice'] = {
                choiceList: choiceList,
                selectedChoiceItemsQL: selectedChoiceItems
            };
            this.chartData[axis]['choice']['choiceList'] = this.defineChoices(this.chartData[axis])
        }, error => { });
    }

    defineChoices(axisData) {
        if (
            axisData.choice && axisData.choice.choiceList &&
            axisData.choice.choiceList.length > 0 &&
            axisData.singleChoice === undefined
        ) {
            return axisData.choice.choiceList;
        } else {
            // if (axisData.isUserProfileData) {
                let master = this.keys.find(x => x.keyword === axisData.userProfileData.type);
                if (master) {
                    if (master.format) {// TODO: need to work
                        let arr: any[] = [];

                        if (master.labels.length > 1) {
                            this[master.name].forEach(x => {
                                x.text = `${x[master.labels[0]]}-${x[master.labels[1]]}`;
                                arr.push(x);
                            });
                        } else {
                            this[master.name].forEach(x => {
                                x.text = `${x[master.labels[0]]}`;
                                arr.push(x);
                            });
                        }
                        return arr;
                    } else {
                        return this[master.name];
                    }
                } else {
                    return [];
                }
            // } else {
            //     return [];
            // }
        }
    }

    _updateMOP(mop) { // check condition of each and assign MOP as needed
        // this.chartData['X'].methodOfOperationId = mop.methodOfOperationId;
        this.chartData['Y'].methodOfOperationId = mop.methodOfOperationId;
        // if (this.chartData['X'].isUserProfileData)
        //     this.chartData['X'].userProfileData.operationId = mop.methodOfOperationId;
        if (this.chartData['Y'].isUserProfileData)
            this.chartData['Y'].userProfileData.operationId = mop.methodOfOperationId;
    }

    _getAxisData(axisData) {
        let chartData: GraphData = new GraphData();
        chartData.questionId = axisData.question.id ? axisData.question.id : undefined;
        chartData.isQuestionData = axisData.questionSelected;
        chartData.isUserProfileData = axisData.profileSelected;
        chartData.methodOfOperationId = this.methodOfOperationId ? this.methodOfOperationId : undefined;
        //FIXME: condition will work only when UPD is applied on X-Axis
        // if (this.chartSelected.type == 'Bar') {
        //     chartData.singleChoice =
        //         axisData.question.choices.filter(choice => choice.selected == true).length === 1 ?
        //             this._fetchSelectedId(axisData.question.choices, 'choiceId') : undefined;
        // }
        // else if (this.chartSelected.type == 'Stacked bar') {
        //     chartData.selectedChoices = axisData.question.choices.filter(choice => choice.selected == true) > 0 ?
        //         axisData.question.choices.filter(choice => choice.selected == true) : undefined;
        // }
        if(axisData.profileSelected)
            chartData.selectedChoices = this._fetchSelectedId(axisData.userProfile.userProfileData, 'dataId');
        else if(axisData.questionSelected)
            chartData.selectedChoices = this._fetchSelectedId(axisData.question.choices, 'choiceId');
        chartData.userProfileData.type = axisData.profileSelected ? GraphAxis.UserDataKeywords
            .find(data => data.sst == axisData.userProfile.userProfile).keyword : undefined;

        chartData.userProfileData.values = axisData.profileSelected ?
            this._fetchSelectedId(axisData.userProfile.userProfileData, 'dataId') : undefined;

        chartData.userProfileData.operationId = this.methodOfOperationId ?
            this.methodOfOperationId : undefined;
        chartData.countryId = this._filterUserProfileData(axisData, 'country');
        chartData.employeeSizeId = this._filterUserProfileData(axisData, 'employee_size');
        chartData.jobTitleId = this._filterUserProfileData(axisData, 'job_title');
        chartData.globalRevenueId = this._filterUserProfileData(axisData, 'revenue');
        chartData.itBudgetId = this._filterUserProfileData(axisData, 'it_budget');
        chartData.industryId = this._filterUserProfileData(axisData, 'industry');
        chartData.lineOfBusinessId = this._filterUserProfileData(axisData, 'line_of_business');
        chartData.geographyId = this._filterUserProfileData(axisData, 'geography');

        return chartData;
    }

    _filterUserProfileData(data, key) {
        if (data.profileSelected && data.userProfile.userProfile == key) {
            return this._fetchSelectedId(data.userProfile.userProfileData, 'dataId');
        } else {
            return undefined;
        }
    }

    _fetchSelectedId(array, key) {
        return array.filter(item => item.selected == true)
            .map(profileSelected => profileSelected[key]);
    }

    openSaveDialog() {
        $("#saveModal").modal('show');
    }

    closeSaveDialog() {
        $("#saveModal").modal('hide');
    }

    onFormSubmit() {
        if (this.saveGraphForm.valid && this.saveGraphForm.dirty && !this.formSubmitted) {
            this.formSubmitted = true;
            let sstData: any = {};
            sstData['labels'] = {};
            sstData['title'] = this.saveGraphForm.value.title;
            sstData['description'] = this.saveGraphForm.value.description;
            sstData['labels']['xAxis'] = this.saveGraphForm.value.xLabel;
            sstData['labels']['yAxis'] = this.saveGraphForm.value.yLabel;
            sstData['isActive'] = true;
            sstData['content'] = this.sstContent;
            sstData['researchDocumentId'] = this.researchModuleId;
            sstData['graphTypeId'] = this.chartData.graphTypeId;
            this.sstService.saveGraph(sstData).subscribe(_ => {
                this.closeSaveDialog();
                if (this.userRole === 'tech-vendor') {
                    this.router.navigate([`${this.userRole}/market-data-detail/${this.researchModuleId}`]);
                } else {
                    this.router.navigate([`${this.userRole}/research-detail/${this.researchModuleId}`]);
                }
            }, error => {
                this.formSubmitted = false;
            })
        } else {
        }
    }

    /**
     * @description function to get selected data to show in the middle area
     * @param data pass selected question or user profile data
     */
    getLabel(data){
      //here
        let str = '';
        //check if question or profile data is selcted for an axis
        if(data && data.questionSelected && data.question.choices &&
          data.question.choices.length > 0 && data.userProfile.title &&
           data.userProfile.userProfileData.length > 0) {
            str += this._getChoiceValue(data.question.label, data.question.choices, 'text',
            data.userProfile.title, data.userProfile.userProfileData, 'title' );
        } else if (data && data.questionSelected && data.question.choices &&
            data.question.choices.length > 0) {
            str += this._getChoiceValue(data.question.label, data.question.choices, 'text');
        } else {
          str += this._getChoiceValue(data.userProfile.title, data.userProfile.userProfileData, 'title');
        }
        return str;
    }

    _getChoiceValue(label, array, key , label2?, array2?, key2?) {
        let str = '';
        let str2 = "";
        str += label + ': ';
        let countSelected = 0;
        let countSelected2 = 0;
        if(array){
        array.forEach((choice, i) => {
            if (choice.selected) {
                str += choice[key] + ', ';
                countSelected++;
            }
            if (i == (array.length -1)) {
              str = str.slice(0, -2);
            }
        });
      }
        if (label2) {
          str2 += ". " + label2 + ': ';
          array2.forEach((choice, i) => {
            if (choice.selected) {
                str2 += choice[key2] + '. ';
                countSelected2++;
            }
            if (i == (array2.length -1)) {
              str2 = str2.slice(0, -2);
            }
        });
        }
        if (countSelected == 0) {
          str = '';
        }
        if (countSelected2 == 0) {
          str2 = '';
        }
        return str + str2;
    }

    resetSST(resetGraph:boolean,newgraphData?:any){
        this.graphGenerated = false;
        this.startOver = true;
        this.filteredYAxisIds = [];
        if(resetGraph){resetGraph
            this.graphTypes = this.graphTypes.map(graph => {
                return {...graph, ...{selectable: true}}
            });
        }
        this.instructionMessageIndex = 0;
        this.xAxisDataValue = '';
        this.yAxisDataValue = '';
        this.canGraphGenerate = false;
        if(newgraphData){
         this.graphSelection(newgraphData);
        }
    }

    resetAxis(axis) {
        if(axis == 'X'){
           this.filteredYAxisIds = [];
            this.resetSST(false);
        }else if(axis ==='Y'){
          this.yAxisDataValue = '';
          this.instructionMessageIndex = 1;
          this.axisData({
            isApplied: true,
            question: {},
            questionSelected: false,
            userProfile: {},
            profileSelected: false
          }, 'Y');
        }
        else{
            this.instructionMessageIndex = 1;
        }
    }

    ngOnDestroy() {
      sessionStorage.setItem("isPrimeCall",'true');
      window.scroll(0,0);
    }

}



// WEBPACK FOOTER //
// ./src/app/feature/main/roles-shared/components/self-service-tab/self-service-tab.component.ts